import { Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { enqueueSnackbar } from "notistack";
import { useForm, FormProvider } from "react-hook-form";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { useGetPartnerQuery } from "src/shared/api/partner";
import { ModalComponent, EConfirmModalType } from "src/shared/components";
import { ContractBody } from "src/shared/components/contracts/ContractBody";
import { modalText } from "src/shared/constants/form.constants";
import { routes } from "src/shared/constants/routes.constants";
import { showApiError } from "src/shared/helpers/api.helpers";
import { useConfirmModal } from "src/shared/hooks";
import { EAgencies } from "src/shared/types/common.types";
import {
  useAttachContractOriginalMutation,
  useAttachTaxCertificateMutation,
  usePostPartnerComplexContractMutation,
} from "../../../model/api/api";
import { getPartnerComplexContractDefaultFormValues } from "../../../model/constants/formValues";
import { IPartnerComplexContractFormValues } from "../../../model/types";
import { ComplexContractDialogFooter } from "./ComplexContractDialogFooter";
import { ComplexContractForm } from "./ComplexContractForm";

export function NewPartnerComplexContract() {
  const { partner_id } = useParams();
  const { data } = useGetPartnerQuery(partner_id || skipToken);
  const navigate = useNavigate();
  const goBackRoute = generatePath(routes.partnerContractsList, { partner_id });

  const [postContract, { isLoading: isLoadingPost }] =
    usePostPartnerComplexContractMutation();
  const [
    attachContractOriginal,
    { isLoading: isLoadingAttachContractOriginal },
  ] = useAttachContractOriginalMutation();
  const [attachTaxCertificate, { isLoading: isLoadingAttachTaxCertificate }] =
    useAttachTaxCertificateMutation();
  const handleSaveContract = async (
    data: IPartnerComplexContractFormValues,
  ) => {
    try {
      const postContractResponse = await postContract({
        ...data,
        partner_id: Number(partner_id),
        contract_date: data.contract_date.format("YYYY-MM-DD"),
      }).unwrap();

      const { partner_id: partnerId, id: contract_id } =
        postContractResponse.data;

      const fileAttachments = [
        {
          handler: attachContractOriginal,
          file: data.contract_original_file_path,
          errorMessage: "Не удалось прикрепить скан договора",
        },
        {
          handler: attachTaxCertificate,
          file: data.tax_certificate_path,
          errorMessage: "Не удалось прикрепить справку о налогообложении",
        },
      ];

      await Promise.all(
        fileAttachments.map(async ({ handler, file, errorMessage }) => {
          if (!file) {
            return;
          }

          try {
            await handler({
              partner_id: partnerId,
              contract_id,
              file,
            });
          } catch (error) {
            showApiError(error);

            enqueueSnackbar(errorMessage, { variant: "warning" });
          }
        }),
      );

      navigate(goBackRoute);
    } catch (e) {
      showApiError(e);
    }
  };

  const methods = useForm<IPartnerComplexContractFormValues>({
    mode: "onChange",
    defaultValues: getPartnerComplexContractDefaultFormValues({
      agency: data?.agency.value,
    }),
  });
  const {
    handleSubmit,
    formState: { isDirty },
  } = methods;
  const { modal, showModal } = useConfirmModal({
    onResolve: () => navigate(goBackRoute),
    confirmModalType: EConfirmModalType.WARNING,
    confirmBtnProps: { isLoading: false, title: modalText.continue },
    children: <Typography>{modalText.unsavedChanges}</Typography>,
    title: modalText.confirmAction,
  });

  return (
    <ModalComponent
      width={() => ({ xs: "100%", sm: "95vw", xxl: 1520 })}
      boxProps={{ sx: { height: "90vh" } }}
      disableBodyPadding
      initialBodyShadow
      title="Новый договор"
      onClose={isDirty ? showModal : () => navigate(goBackRoute)}
      open
      modalFooter={
        <ComplexContractDialogFooter
          isLoading={
            isLoadingPost ||
            isLoadingAttachContractOriginal ||
            isLoadingAttachTaxCertificate
          }
          onSubmit={handleSubmit(handleSaveContract)}
          onCancel={isDirty ? showModal : () => navigate(goBackRoute)}
        />
      }
    >
      {modal}
      <FormProvider {...methods}>
        <ContractBody
          text=""
          form={
            <ComplexContractForm
              agency={data?.agency.value || EAgencies.IContext}
            />
          }
        />
      </FormProvider>
    </ModalComponent>
  );
}
